<template>
  <div class="org-page">
    <div class="filter-card">
      <div class="filter-card__body">
        <div class="filter-card__heading">
          <span class="filter-card__title">{{ $t('org.my.title') }}</span>
          <span class="filter-card__sub">{{ $t('org.my.tip') }}</span>
        </div>
        <span class="filter-card__actions">
          <a-button type="primary" icon="plus" :disabled="hasTopOrg" @click="openCreate">{{ $t('org.create.button') }}</a-button>
          <a-button icon="team" @click="openJoin">{{ $t('org.join.button') }}</a-button>
          <a-button icon="reload" @click="loadOrgs">{{ $t('org.refresh') }}</a-button>
        </span>
      </div>
    </div>

    <div class="table-card">
      <a-table
        :columns="columns"
        :data-source="orgs"
        :loading="loading"
        :pagination="false"
        :locale="locale"
        row-key="id"
        :key="tableKey"
        :indent-size="40"
        default-expand-all-rows>
        <span slot="admin_count" slot-scope="text, record">
          <a
            class="org-admin-count"
            :class="record.admin_count > 0 ? 'org-admin-count--active' : 'org-admin-count--zero'"
            @click="openAdmins(record)">{{ record.admin_count }}<a-icon type="right" class="org-admin-count__arrow" /></a>
        </span>
        <div slot="action" slot-scope="text, record">
          <a-button type="link" icon="apartment" @click="openCreateSub(record)">{{ $t('org.create.sub.button') }}</a-button>
          <a-button type="link" icon="user-add" @click="openAddAdmin(record)">{{ $t('org.add.admin.button') }}</a-button>
          <a-button v-if="record.is_owner" type="link" icon="edit" @click="openRename(record)">{{ $t('org.rename.button') }}</a-button>
          <a-button v-if="record.is_owner" type="link" icon="sync" @click="changeInviteCode(record)">{{ $t('org.invite.change.button') }}</a-button>
          <a-button v-if="record.is_owner" type="link" icon="delete" class="org-delete" @click="openDelete(record)">{{ $t('org.delete.button') }}</a-button>
        </div>
      </a-table>
    </div>

    <!-- 修改组织名称 -->
    <a-modal
      v-model="renameVisible"
      :title="$t('org.rename.modal.title')"
      :confirm-loading="renameLoading"
      :mask-closable="false"
      @ok="handleRename">
      <a-input
        v-model="renameName"
        size="large"
        :placeholder="$t('org.rename.placeholder')"
        allow-clear
        @pressEnter="handleRename" />
    </a-modal>

    <!-- 添加管理员 -->
    <a-modal
      v-model="addAdminVisible"
      :title="$t('org.add.admin.modal.title')"
      :confirm-loading="addAdminLoading"
      :mask-closable="false"
      @ok="handleAddAdmin">
      <p class="org-add-admin-tip">{{ $t('org.add.admin.tip') }}</p>
      <a-form :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
        <a-form-item :label="$t('user.account')">
          <a-input v-model="addAdminAccount" :placeholder="$t('org.add.admin.account.placeholder')" allow-clear @pressEnter="handleAddAdmin" />
        </a-form-item>
        <a-form-item :label="$t('user.password')">
          <a-input-password v-model="addAdminPasswd" :placeholder="$t('org.add.admin.passwd.placeholder')" @pressEnter="handleAddAdmin" />
        </a-form-item>
        <a-form-item :label="$t('user.password.confirm')">
          <a-input-password v-model="addAdminPasswd2" :placeholder="$t('user.password.confirm.tip')" @pressEnter="handleAddAdmin" />
        </a-form-item>
        <a-form-item :label="$t('user.name')">
          <a-input v-model="addAdminName" :placeholder="$t('org.add.admin.name.placeholder')" allow-clear @pressEnter="handleAddAdmin" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 管理员管理 -->
    <a-modal
      v-model="adminsVisible"
      :title="adminsOrg ? $t('org.admins.modal.title.with.org').replace('{name}', adminsOrg.name) : $t('org.admins.modal.title')"
      :footer="null"
      :mask-closable="false"
      :width="840">
      <div class="admins-toolbar">
        <span class="admins-toolbar__tip">{{ $t('org.admins.tip') }}</span>
        <a-button v-if="adminsOrg && adminsOrg.is_owner" type="primary" icon="user-add" size="small" @click="openAddAdmin(adminsOrg)">{{ $t('org.add.admin.button') }}</a-button>
      </div>
      <a-table
        :columns="adminColumns"
        :data-source="admins"
        :loading="adminsLoading"
        :pagination="false"
        :locale="locale"
        row-key="uid"
        size="small">
        <span slot="status" slot-scope="text, record">
          <a-switch
            :checked="record.status === 1"
            :disabled="!(adminsOrg && adminsOrg.is_owner) || record.is_creator"
            @change="checked => toggleAdminStatus(record, checked)" />
        </span>
        <div slot="action" slot-scope="text, record">
          <a-button
            v-if="adminsOrg && adminsOrg.is_owner && !record.is_creator"
            type="link"
            icon="swap"
            size="small"
            @click="openChangeOrg(record)">{{ $t('org.admins.change.org') }}</a-button>
          <a-button
            v-if="adminsOrg && adminsOrg.is_owner && !record.is_creator"
            type="link"
            icon="edit"
            size="small"
            @click="openAdminRename(record)">{{ $t('org.admins.rename') }}</a-button>
          <a-button
            v-if="adminsOrg && adminsOrg.is_owner && !record.is_creator"
            type="link"
            icon="delete"
            size="small"
            class="org-delete"
            @click="deleteAdmin(record)">{{ $t('org.admins.delete') }}</a-button>
          <span v-if="record.is_creator" class="admins-creator">{{ $t('org.admins.creator') }}</span>
        </div>
      </a-table>
    </a-modal>

    <!-- 修改管理员姓名 -->
    <a-modal
      v-model="adminRenameVisible"
      :title="$t('org.admins.rename.modal.title')"
      :confirm-loading="adminRenameLoading"
      :mask-closable="false"
      @ok="handleAdminRename">
      <a-input v-model="adminRenameName" size="large" :placeholder="$t('org.admins.rename.placeholder')" allow-clear @pressEnter="handleAdminRename" />
    </a-modal>

    <!-- 修改管理员所在组织 -->
    <a-modal
      v-model="changeOrgVisible"
      :title="$t('org.admins.change.org.modal.title')"
      :confirm-loading="changeOrgLoading"
      :mask-closable="false"
      @ok="handleChangeOrg">
      <a-select
        v-model="changeOrgTarget"
        size="large"
        style="width: 100%"
        :placeholder="$t('org.admins.change.org.placeholder')">
        <a-select-option v-for="org in changeOrgOptions" :key="org.id" :value="org.id">
          {{ org.name }}
        </a-select-option>
      </a-select>
    </a-modal>

    <!-- 删除组织: 输入组织名称确认 -->
    <a-modal
      v-model="deleteVisible"
      :title="$t('org.delete.modal.title')"
      :confirm-loading="deleteLoading"
      :mask-closable="false"
      :ok-button-props="{ disabled: !deleteConfirmOk }"
      @ok="handleDelete">
      <p class="org-delete-tip">
        {{ $t('org.delete.confirm.prefix') }}
        <span class="org-delete-name">{{ deleteOrg ? deleteOrg.name : '' }}</span>
        {{ $t('org.delete.confirm.suffix') }}
      </p>
      <a-input
        v-model="deleteName"
        size="large"
        :placeholder="$t('org.delete.confirm.placeholder')"
        allow-clear
        @pressEnter="handleDelete" />
    </a-modal>

    <!-- 创建组织 / 创建下级组织 -->
    <a-modal
      v-model="createVisible"
      :title="createParentOrg ? $t('org.create.sub.modal.title').replace('{name}', createParentOrg.name) : $t('org.create.modal.title')"
      :confirm-loading="createLoading"
      :mask-closable="false"
      @ok="handleCreate">
      <a-input
        v-model="createName"
        size="large"
        :placeholder="createParentId ? $t('org.create.sub.placeholder') : $t('org.create.placeholder')"
        allow-clear
        @pressEnter="handleCreate" />
    </a-modal>

    <!-- 加入组织 -->
    <a-modal
      v-model="joinVisible"
      :title="$t('org.join.modal.title')"
      :confirm-loading="joinLoading"
      :mask-closable="false"
      @ok="handleJoin">
      <a-input
        v-model="inviteCode"
        size="large"
        :placeholder="$t('org.join.placeholder')"
        allow-clear
        @pressEnter="handleJoin" />
      <p v-if="joinOrgLoading" class="org-join-preview org-join-preview--muted">{{ $t('org.join.searching') }}</p>
      <p v-else-if="joinOrgPreview" class="org-join-preview">{{ $t('org.join.org.name').replace('{name}', joinOrgPreview.name) }}</p>
      <p v-else-if="inviteCode && inviteCode.trim()" class="org-join-preview org-join-preview--error">{{ $t('org.join.not.found') }}</p>
    </a-modal>
  </div>
</template>

<script>
import { changeOrganizationAdminOrg, changeOrganizationInviteCode, createOrganization, createOrganizationAdmin, deleteOrganization, deleteOrganizationAdmin, joinOrganization, loadMyOrganization, loadOrganizationAdmins, loadOrganizationByInviteCode, toggleOrganizationAdminStatus, updateOrganizationAdminName, updateOrganizationName } from '@/api/data'
import { getI18nLanguage } from '@/locales'

export default {
  name: 'Organization',
  data () {
    return {
      loading: false,
      orgs: [],
      tableKey: 0,
      hasTopOrg: false,
      createVisible: false,
      createLoading: false,
      createName: '',
      createParentId: null,
      createParentOrg: null,
      joinVisible: false,
      joinLoading: false,
      inviteCode: '',
      joinOrgPreview: null,
      joinOrgLoading: false,
      lookupTimer: null,
      deleteVisible: false,
      deleteLoading: false,
      deleteOrg: null,
      deleteName: '',
      renameVisible: false,
      renameLoading: false,
      renameOrg: null,
      renameName: '',
      addAdminVisible: false,
      addAdminOrg: null,
      addAdminLoading: false,
      addAdminAccount: '',
      addAdminPasswd: '',
      addAdminPasswd2: '',
      addAdminName: '',
      adminsVisible: false,
      adminsOrg: null,
      adminsLoading: false,
      admins: [],
      adminRenameVisible: false,
      adminRenameLoading: false,
      adminRenameUid: null,
      adminRenameName: '',
      changeOrgVisible: false,
      changeOrgLoading: false,
      changeOrgAdminUid: null,
      changeOrgTarget: null,
      allOrgs: [],
      adminColumns: [{
        title: this.$t('user.name'),
        dataIndex: 'name',
        key: 'name',
        align: 'center'
      }, {
        title: this.$t('user.account'),
        dataIndex: 'account',
        key: 'account',
        align: 'center'
      }, {
        title: this.$t('org.admins.status'),
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        scopedSlots: { customRender: 'status' }
      }, {
        title: this.$t('org.action'),
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        scopedSlots: { customRender: 'action' }
      }],
      locale: {
        emptyText: this.$t('org.empty.tip')
      },
      columns: [{
        title: this.$t('org.name'),
        dataIndex: 'name',
        key: 'name',
        align: 'left',
        width: 240
      }, {
        title: this.$t('org.admin.count'),
        dataIndex: 'admin_count',
        key: 'admin_count',
        width: 100,
        align: 'center',
        scopedSlots: { customRender: 'admin_count' }
      }, {
        title: this.$t('org.driver.count'),
        dataIndex: 'driver_count',
        key: 'driver_count',
        width: 100,
        align: 'center'
      }, {
        title: this.$t('org.create.time'),
        dataIndex: 'create_time',
        key: 'create_time',
        align: 'center'
      }, {
        title: this.$t('org.action'),
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        scopedSlots: { customRender: 'action' }
      }]
    }
  },
  watch: {
    inviteCode (val) {
      this.lookupInviteOrg(val)
    }
  },
  computed: {
    deleteConfirmOk () {
      return !!(this.deleteName && this.deleteOrg && this.deleteName.trim() === this.deleteOrg.name)
    },
    changeOrgOptions () {
      return this.allOrgs.filter(o => o.id !== (this.adminsOrg && this.adminsOrg.id))
    }
  },
  created () {
    this.loadOrgs()
  },
  methods: {
    getErrorMessage (msg) {
      if (typeof msg === 'string') {
        return msg
      }
      const lang = getI18nLanguage()
      const langKey = lang.replace('-', '').toLowerCase()
      if (langKey.includes('zh')) {
        return msg['zh'] || msg['zh-CN'] || msg['en'] || msg['en-US'] || ''
      } else if (langKey.includes('ja')) {
        return msg['ja'] || msg['ja-JP'] || msg['en'] || msg['en-US'] || ''
      }
      return msg['en'] || msg['en-US'] || ''
    },
    loadOrgs () {
      this.loading = true
      loadMyOrganization({}).then(res => {
        if (res.code === 0) {
          const list = (res.result && res.result.orgs) || []
          this.allOrgs = list
          this.hasTopOrg = list.some(o => Number(o.parent_id) === 0)
          this.orgs = this.buildTree(list)
          this.tableKey++
        }
      }).finally(() => {
        this.loading = false
      })
    },
    buildTree (orgs) {
      const map = {}
      orgs.forEach(o => {
        o.children = []
        map[o.id] = o
      })
      const roots = []
      orgs.forEach(o => {
        if (o.parent_id && map[o.parent_id]) {
          map[o.parent_id].children.push(o)
        } else {
          roots.push(o)
        }
      })
      return roots
    },
    openCreate () {
      this.createName = ''
      this.createParentId = null
      this.createParentOrg = null
      this.createVisible = true
    },
    openCreateSub (record) {
      this.createName = ''
      this.createParentId = record.id
      this.createParentOrg = record
      this.createVisible = true
    },
    handleCreate () {
      const name = (this.createName || '').trim()
      if (!name) {
        this.$message.warning(this.$t('org.create.name.required'))
        return
      }
      this.createLoading = true
      createOrganization({ name, parent_id: this.createParentId }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.create.success'))
          this.createVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.create.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.create.fail'))
      }).finally(() => {
        this.createLoading = false
      })
    },
    openJoin () {
      this.inviteCode = ''
      this.joinVisible = true
    },
    lookupInviteOrg (val) {
      clearTimeout(this.lookupTimer)
      const code = (val || '').trim()
      this.joinOrgPreview = null
      this.joinOrgLoading = false
      if (!code) {
        return
      }
      this.joinOrgLoading = true
      this.lookupTimer = setTimeout(() => {
        loadOrganizationByInviteCode({ invite_code: code }).then(res => {
          this.joinOrgLoading = false
          if (res.code === 0 && res.result && res.result.org) {
            this.joinOrgPreview = res.result.org
          } else {
            this.joinOrgPreview = null
          }
        }).catch(() => {
          this.joinOrgLoading = false
          this.joinOrgPreview = null
        })
      }, 400)
    },
    handleJoin () {
      const code = (this.inviteCode || '').trim()
      if (!code) {
        this.$message.warning(this.$t('org.join.code.required'))
        return
      }
      this.joinLoading = true
      joinOrganization({ invite_code: code }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.join.success'))
          this.joinVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.join.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.join.fail'))
      }).finally(() => {
        this.joinLoading = false
      })
    },
    changeInviteCode (record) {
      changeOrganizationInviteCode({ org_id: record.id }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.invite.change.success'))
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.invite.change.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.invite.change.fail'))
      })
    },
    openRename (record) {
      this.renameOrg = record
      this.renameName = record.name
      this.renameVisible = true
    },
    handleRename () {
      const name = (this.renameName || '').trim()
      if (!name) {
        this.$message.warning(this.$t('org.rename.name.required'))
        return
      }
      this.renameLoading = true
      updateOrganizationName({ org_id: this.renameOrg.id, name }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.rename.success'))
          this.renameVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.rename.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.rename.fail'))
      }).finally(() => {
        this.renameLoading = false
      })
    },
    openAddAdmin (record) {
      this.addAdminOrg = record
      this.addAdminAccount = ''
      this.addAdminPasswd = ''
      this.addAdminPasswd2 = ''
      this.addAdminName = ''
      this.addAdminVisible = true
    },
    handleAddAdmin () {
      const account = (this.addAdminAccount || '').trim()
      const passwd = this.addAdminPasswd || ''
      if (!account) {
        this.$message.warning(this.$t('org.add.admin.account.required'))
        return
      }
      if (!/^[\w.+-]+@[\w-]+(\.[\w-]+)+$/.test(account)) {
        this.$message.warning(this.$t('org.add.admin.account.format'))
        return
      }
      if (!passwd || passwd.length < 6) {
        this.$message.warning(this.$t('org.add.admin.passwd.min'))
        return
      }
      if (passwd !== (this.addAdminPasswd2 || '')) {
        this.$message.warning(this.$t('user.password.confirm.error'))
        return
      }
      this.addAdminLoading = true
      createOrganizationAdmin({
        org_id: this.addAdminOrg.id,
        account,
        passwd,
        name: (this.addAdminName || '').trim()
      }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.add.admin.success'))
          this.addAdminVisible = false
          this.loadOrgs()
          if (this.adminsVisible) {
            this.loadAdmins()
          }
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.add.admin.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.add.admin.fail'))
      }).finally(() => {
        this.addAdminLoading = false
      })
    },
    openAdmins (record) {
      this.adminsOrg = record
      this.adminsVisible = true
      this.loadAdmins()
    },
    loadAdmins () {
      if (!this.adminsOrg) {
        return
      }
      this.adminsLoading = true
      loadOrganizationAdmins({ org_id: this.adminsOrg.id }).then(res => {
        if (res.code === 0) {
          this.admins = (res.result && res.result.admins) || []
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.load.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.admins.load.fail'))
      }).finally(() => {
        this.adminsLoading = false
      })
    },
    toggleAdminStatus (record, checked) {
      toggleOrganizationAdminStatus({ org_id: this.adminsOrg.id, uid: record.uid, status: checked ? 1 : 0 }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.admins.status.success'))
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.status.fail'))
        }
        this.loadAdmins()
      }).catch(() => {
        this.$message.error(this.$t('org.admins.status.fail'))
        this.loadAdmins()
      })
    },
    deleteAdmin (record) {
      this.$confirm({
        title: this.$t('org.admins.delete.confirm'),
        onOk: () => {
          return deleteOrganizationAdmin({ org_id: this.adminsOrg.id, uid: record.uid }).then(res => {
            if (res.code === 0) {
              this.$message.success(this.$t('org.admins.delete.success'))
              this.loadAdmins()
              this.loadOrgs()
            } else {
              this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.delete.fail'))
            }
          }).catch(() => {
            this.$message.error(this.$t('org.admins.delete.fail'))
          })
        }
      })
    },
    openAdminRename (record) {
      this.adminRenameUid = record.uid
      this.adminRenameName = record.name
      this.adminRenameVisible = true
    },
    handleAdminRename () {
      const name = (this.adminRenameName || '').trim()
      if (!name) {
        this.$message.warning(this.$t('org.admins.rename.name.required'))
        return
      }
      this.adminRenameLoading = true
      updateOrganizationAdminName({ org_id: this.adminsOrg.id, uid: this.adminRenameUid, name }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.admins.rename.success'))
          this.adminRenameVisible = false
          this.loadAdmins()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.rename.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.admins.rename.fail'))
      }).finally(() => {
        this.adminRenameLoading = false
      })
    },
    openChangeOrg (record) {
      this.changeOrgAdminUid = record.uid
      this.changeOrgTarget = null
      this.changeOrgVisible = true
    },
    handleChangeOrg () {
      if (!this.changeOrgTarget) {
        this.$message.warning(this.$t('org.admins.change.org.required'))
        return
      }
      this.changeOrgLoading = true
      changeOrganizationAdminOrg({
        org_id: this.adminsOrg.id,
        uid: this.changeOrgAdminUid,
        target_org_id: this.changeOrgTarget
      }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.admins.change.org.success'))
          this.changeOrgVisible = false
          this.loadAdmins()
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.change.org.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.admins.change.org.fail'))
      }).finally(() => {
        this.changeOrgLoading = false
      })
    },
    openDelete (record) {
      this.deleteOrg = record
      this.deleteName = ''
      this.deleteVisible = true
    },
    handleDelete () {
      if (!this.deleteConfirmOk) {
        this.$message.warning(this.$t('org.delete.name.not.match'))
        return
      }
      this.deleteLoading = true
      deleteOrganization({ org_id: this.deleteOrg.id }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.delete.success'))
          this.deleteVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.delete.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.delete.fail'))
      }).finally(() => {
        this.deleteLoading = false
      })
    }
  }
}
</script>

<style scoped lang="less">
@primary: #52C41A;
@radius: 6px;
@border-color: #e8e8e8;
@text-primary: rgba(0, 0, 0, 0.85);
@shadow-card: 0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);

.org-page {
  margin: 0 auto;
}

.filter-card {
  background: #fff;
  border-radius: @radius;
  box-shadow: @shadow-card;
  padding: 16px 24px;
  margin-bottom: 16px;

  &__body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: @text-primary;
  }

  &__heading {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__sub {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}

.table-card {
  background: #fff;
  border-radius: @radius;
  box-shadow: @shadow-card;
  padding: 12px 16px;
}

.org-delete {
  color: #ff4d4f;
}

.org-admin-count {
  display: inline-flex;
  align-items: center;
  font-weight: 600;

  &__arrow {
    margin-left: 3px;
    font-size: 8px;
    line-height: 1;
  }

  &--active {
    min-width: 28px;
    padding: 1px 10px;
    border-radius: 12px;
    color: #fff;
    background: @primary;
    text-align: center;
    transition: background 0.2s;

    &:hover {
      background: darken(@primary, 8%);
    }
  }

  &--zero {
    color: rgba(0, 0, 0, 0.45);

    &:hover {
      color: @primary;
    }
  }
}

.admins-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  &__tip {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
  }
}

.admins-creator {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.org-delete-tip {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 16px;
}

.org-delete-name {
  color: #ff4d4f;
  font-weight: 600;
}

.org-join-preview {
  font-size: 13px;
  margin-top: 12px;
  color: @primary;

  &--muted {
    color: rgba(0, 0, 0, 0.45);
  }

  &--error {
    color: #ff4d4f;
  }
}

.org-add-admin-tip {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 16px;
}
</style>
